import React from 'react';
import PropTypes from 'prop-types';
import css from './Header.scss';
import preferenceCss from './PreferencesSection.scss';
import { imageStyle } from 'utils/images';
import { Card } from 'semantic-ui-react';
import { getWord } from 'utils/tenant';
import PhotoUploadLink from './PhotoUploadLink';
import classnames from 'classnames';
import { useWindowSize } from 'react-use';
import { isMobileScreen } from 'utils/window';
import { isEmptyString } from 'utils/text';
import ProfileFullName from 'common/ReadMoreLessText';

/**
 * @returns {JSX.Element} the profile component that shows the user's basic information, photo and allows them
 * to upload a new photo (if permissions allow).
 */
const Header = ({ allowPhotoUpload, firstName, hasPhoto, isIE, isStaffWithoutStudentRole, lastName, photoAcceptTypes,
                    pronouns, schoolEmail, showPronouns, userName, uuid, validatePhoto }) => {
    // Monitor window width
    const { width } = useWindowSize();

    const getPronouns = () => {
        if(!showPronouns || isStaffWithoutStudentRole || isEmptyString(pronouns)) return null;
        return <div aria-label="pronouns" className={css['pronouns']} role="text">({pronouns})</div>;
    }

    if (isMobileScreen(width)) return (
        <>
            <div className={css['mobile-header']}>
                <div id="profilePhoto" className={css['image']} style={imageStyle(uuid, hasPhoto)} />
                <div className={css['content']}>
                    <ProfileFullName
                        text={firstName + ' ' + lastName}
                        customCss={{
                            fullCss: css['full'],
                            fixedCss: css['fixed'],
                            hasMoreTextCss: css['span-has-more-text'],
                            showMoreCss: css['more'],
                            showLessCss: css['less']
                        }}
                    />
                    { getPronouns() }
                    <PhotoUploadLink allowPhotoUpload={allowPhotoUpload}
                                     isIE={isIE}
                                     photoAcceptTypes={photoAcceptTypes}
                                     photoElementId="profilePhoto"
                                     uuid={uuid}
                                     validatePhoto={validatePhoto} />
                </div>
            </div>
            <hr className={css['divider']} />
        </>
    );
    
    return (
        <div className={css['header-card']}>
            <h1>Edit Profile</h1>
            <Card className={preferenceCss['profile-card']}>
                <div className={css['header']}>
                    <div className={css['photo']}>
                        <div id="profilePhoto" className={css['image']} style={imageStyle(uuid, hasPhoto)} />
                        <PhotoUploadLink allowPhotoUpload={allowPhotoUpload}
                                         isIE={isIE}
                                         photoAcceptTypes={photoAcceptTypes}
                                         photoElementId="profilePhoto"
                                         uuid={uuid}
                                         validatePhoto={validatePhoto} />
                    </div>
                    <div className={css['content']}>
                        <ProfileFullName
                            text={firstName + ' ' + lastName}
                            customCss={{
                                fullCss: css['full'],
                                fixedCss: css['fixed'],
                                hasMoreTextCss: css['span-has-more-text'],
                                showMoreCss: css['more'],
                                showLessCss: css['less']
                            }}
                        />
                        { getPronouns() }
                        <div className={css['supplemental-info']}>
                            <span>
                                <label>Username</label>
                                <br/>
                                <div className={classnames(css['info'], preferenceCss['overflow-fade'], { [preferenceCss.ie]: isIE })}>{userName}</div>
                            </span>
                            <span>
                                <label>{getWord('institutionEmail')}</label>
                                <br/>
                                <div className={classnames(css['info'], preferenceCss['overflow-fade'], { [preferenceCss.ie]: isIE })}>{schoolEmail}</div>
                            </span>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    )
};

Header.defaultProps = {
    isStaffWithoutStudentRole: false,
    showPronouns: false
}

Header.propTypes = {
    allowPhotoUpload: PropTypes.bool,
    firstName: PropTypes.string,
    hasPhoto: PropTypes.bool,
    isIE: PropTypes.bool,
    isStaffWithoutStudentRole: PropTypes.bool,
    lastName: PropTypes.string,
    photoAcceptTypes: PropTypes.string,
    pronouns: PropTypes.string,
    schoolEmail: PropTypes.string,
    showPronouns: PropTypes.bool,
    userName: PropTypes.string,
    uuid: PropTypes.string,
    validatePhoto: PropTypes.func.isRequired
};

export default Header;
